import { lerp, clamp } from "@cc/maths-js";
import { Tween } from "../animation/Tween";
import { EventEmitter } from "eventemitter3";
import { TweenOptions } from "../animation/TweenOptions";
import { RgbaConfig } from "./RgbaConfig";

export class ColorRgba extends EventEmitter {
  public static parse(rgba: RgbaConfig | string | number, target?: RgbaConfig): RgbaConfig {
    if (typeof rgba === "string") rgba = ColorRgba.hexToRgba(rgba);
    if (typeof rgba === "number") rgba = ColorRgba.numToRgba(rgba);
    target = target || {};
    target.r = typeof rgba.r === "number" ? rgba.r : 0;
    target.g = typeof rgba.g === "number" ? rgba.g : 0;
    target.b = typeof rgba.b === "number" ? rgba.b : 0;
    target.a = typeof rgba.a === "number" ? rgba.a : 1;
    return target;
  }

  public static numToRgba(num: number) {
    const rgba: RgbaConfig = {};
    rgba.r = Math.floor(num / (256 * 256)) % 256;
    rgba.g = Math.floor(num / 256) % 256;
    rgba.b = num % 256;
    rgba.a = 1;
    return rgba;
  }

  public static hexToRgba(hex: string) {
    const num = ColorRgba.hexToNum(hex);
    return ColorRgba.numToRgba(num);
  }

  public static rgbToHex(rgba: RgbaConfig): string {
    var rg = rgba.b | (rgba.g << 8) | (rgba.r << 16);
    return "#" + (0x1000000 + rg).toString(16).slice(1);
  }

  public static rgbToNum(rgba: RgbaConfig): number {
    const hex = ColorRgba.rgbToHex(rgba);
    return ColorRgba.hexToNum(hex);
  }

  public static hexToNum(hex: string): number {
    return parseInt(hex.replace("#", "0x"));
  }

  public r: number;
  public g: number;
  public b: number;
  public a: number;
  public get hex() {
    return ColorRgba.rgbToHex(this);
  }
  public get num() {
    return ColorRgba.rgbToNum(this);
  }
  public set hex(hex: string) {
    this.set(hex);
  }
  public set num(num: number) {
    this.set(num);
  }

  private k: number = 0;
  private tween: Tween;
  private target: RgbaConfig = {};
  private from: RgbaConfig = {};

  constructor(rgba?: RgbaConfig | string | number) {
    super();
    rgba = rgba || {};
    ColorRgba.parse(rgba, this.target);
    ColorRgba.parse(rgba, this.from);
    ColorRgba.parse(rgba, this);
  }

  public clone(): ColorRgba {
    return new ColorRgba({
      r: this.r,
      g: this.g,
      b: this.b,
      a: this.a
    });
  }

  public set(rgba?: RgbaConfig | string | number) {
    Tween.removeTweens(this);
    ColorRgba.parse(rgba, this.target);
    ColorRgba.parse(rgba, this.from);
    ColorRgba.parse(rgba, this);
    this.emit("set", this);
  }

  public add(value: number): ColorRgba {
    this.r = clamp(this.r + value, 0, 255);
    this.g = clamp(this.g + value, 0, 255);
    this.b = clamp(this.b + value, 0, 255);
    return this;
  }

  public mult(value: number): ColorRgba {
    this.r = clamp(this.r * value, 0, 255);
    this.g = clamp(this.g * value, 0, 255);
    this.b = clamp(this.b * value, 0, 255);
    return this;
  }

  public destroy() {
    Tween.removeTweens(this);
  }

  /**
   */
  public to(rgba: RgbaConfig | string | number, durationS?: number, options?: TweenOptions) {
    ColorRgba.parse(this, this.from);
    ColorRgba.parse(rgba, this.target);
    Tween.removeTweens(this);
    const d = (durationS || 0) * 1000;
    this.tween = Tween.get(this, options);
    this.k = 0;
    this.tween //
      .to({ k: 1 }, d, createjs.Ease.sineOut)
      .on("change", this.onAnimChange.bind(this));
  }

  private onAnimChange() {
    this.r = lerp(this.from.r, this.target.r, this.k).toFixed(6);
    this.g = lerp(this.from.g, this.target.g, this.k).toFixed(6);
    this.b = lerp(this.from.b, this.target.b, this.k).toFixed(6);
    this.a = lerp(this.from.a, this.target.a, this.k).toFixed(6);
    this.emit("set", this);
  }
}
